/**
 * Kiks Product Card - Color Variant Limiter
 * Limits color variants to 4 items, shows "+X" indicator when not hovering, 
 * and reveals hidden variants inline on container hover
 */

class KiksColorVariantLimiter {
    constructor() {
        this.maxVisibleVariants = 4;
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupVariantLimiters());
        } else {
            this.setupVariantLimiters();
        }
    }

    setupVariantLimiters() {
        // Find all color variant containers
        const variantContainers = document.querySelectorAll('.kiks-product-card .options-container');

        variantContainers.forEach(container => {
            this.processVariantContainer(container);
        });

        // Also handle dynamically added content
        this.observeNewContent();
    }

    processVariantContainer(container) {
        // Find color swatch fieldsets
        const colorSwatchFieldsets = container.querySelectorAll('fieldset.product-form__input');

        colorSwatchFieldsets.forEach(fieldset => {
            const swatchContainer = fieldset.querySelector('.inline-flex.flex-wrap');
            if (!swatchContainer) return;

            const colorSwatches = swatchContainer.querySelectorAll('.color-watches');
            if (colorSwatches.length <= this.maxVisibleVariants) return;

            this.limitColorSwatches(swatchContainer, colorSwatches);
        });
    }

    limitColorSwatches(container, swatches) {
        const totalSwatches = swatches.length;
        const hiddenCount = totalSwatches - this.maxVisibleVariants;

        // Hide excess swatches initially
        for (let i = this.maxVisibleVariants; i < totalSwatches; i++) {
            swatches[i].style.display = 'none';
            swatches[i].classList.add('hidden-variant');
        }

        // Create overflow indicator
        const overflowIndicator = this.createOverflowIndicator(hiddenCount);

        // Insert overflow indicator after the last visible swatch
        const lastVisibleSwatch = swatches[this.maxVisibleVariants - 1];
        lastVisibleSwatch.parentNode.insertBefore(overflowIndicator, lastVisibleSwatch.nextSibling);

        // Add hover events to show/hide variants inline on the container
        this.addInlineHoverEvents(container, swatches, hiddenCount, overflowIndicator);
    }

    createOverflowIndicator(hiddenCount) {
        const indicator = document.createElement('div');
        indicator.className = 'kiks-indicator color-watches overflow-indicator cursor-pointer block swatch-32 relative rounded-full';
        indicator.style.cssText = `
            width: 32px;
            height: 32px;
            background: transparent;
            box-shadow: 0 0 0 2px #D2D2D2, 0 0 0 2px rgba(var(--colors-line-and-border), 1);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: 700;
            padding: 5px;
            color: #4B4C4E;
            transition: 0.2s;
        `;
        indicator.innerHTML = `+${hiddenCount}`;
        indicator.setAttribute('aria-label', `${hiddenCount} more color variants`);

        return indicator;
    }

    addInlineHoverEvents(container, swatches, hiddenCount, overflowIndicator) {
        let hoverTimeout;

        // Add hover events to the container to show/hide variants
        container.addEventListener('mouseenter', () => {
            clearTimeout(hoverTimeout);
            this.showHiddenVariants(swatches, overflowIndicator);
        });

        container.addEventListener('mouseleave', () => {
            hoverTimeout = setTimeout(() => {
                this.hideHiddenVariants(swatches, overflowIndicator);
            }, 100);
        });
    }

    showHiddenVariants(swatches, overflowIndicator) {
        // Show all hidden variants
        for (let i = this.maxVisibleVariants; i < swatches.length; i++) {
            swatches[i].style.display = 'block';
            swatches[i].classList.add('showing-hidden');
        }

        // Hide the overflow indicator when showing variants
        if (overflowIndicator) {
            overflowIndicator.style.display = 'none';
        }
    }

    hideHiddenVariants(swatches, overflowIndicator) {
        // Hide all hidden variants
        for (let i = this.maxVisibleVariants; i < swatches.length; i++) {
            swatches[i].style.display = 'none';
            swatches[i].classList.remove('showing-hidden');
        }

        // Show the overflow indicator when hiding variants
        if (overflowIndicator) {
            overflowIndicator.style.display = 'flex';
        }
    }

    observeNewContent() {
        // Watch for new product cards being added dynamically
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.classList && node.classList.contains('kiks-product-card')) {
                            this.processVariantContainer(node);
                        } else {
                            const productCards = node.querySelectorAll && node.querySelectorAll('.kiks-product-card');
                            if (productCards) {
                                productCards.forEach(card => this.processVariantContainer(card));
                            }
                        }
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

// Initialize the color variant limiter
new KiksColorVariantLimiter();
